import React, { useEffect, useState } from "react";
import { AgGridReact } from "ag-grid-react";
import {
    getbudgetvsactual,
    listfinancialyears
} from "../../../../services/admin";
import { toast } from "react-toastify";
import moment from "moment";
import baseUrl from "../../../../Api";
import { printReportUrl } from "../../../../utils/printPdf";

export default function BudgetVsActual() {
    const [reportData, setReportData] = useState(null);
    const [financialYears, setFinancialYears] = useState([]);
    const [loading, setLoading] = useState(false);
    const branchId = localStorage.getItem("branch") || "";

    const [filters, setFilters] = useState({
        financial_year_id: "",
        period_type: "",
        start_date: moment(new Date()).format("YYYY-MM-DD"),
        end_date: moment(new Date()).format("YYYY-MM-DD")
    });

    useEffect(() => {
        fetchFinancialYears();
    }, []);

    const fetchFinancialYears = () => {
        listfinancialyears().then((res) => {
            if (res.status === true) {
                setFinancialYears(res.data);
                // Auto-select active year
                const activeYear = res.data.find(y => y.is_active);
                if (activeYear) {
                    setFilters(prev => ({ ...prev, financial_year_id: activeYear.id }));
                }
            }
        });
    };

    const fetchReport = () => {
        if (!filters.financial_year_id) {
            toast.error("Please select a financial year");
            return;
        }

        setLoading(true);
        const params = {
            ...filters,
            branch_id: branchId
        };
        getbudgetvsactual(params).then((res) => {
            if (res.status === true) {
                setReportData(res.data);
            } else {
                toast.error(res.message || "Failed to fetch report");
            }
            setLoading(false);
        });
    };

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const getStatusBadge = (status) => {
        if (status === 'Good') {
            return <span className="badge bg-success">Good</span>;
        } else if (status === 'Warning') {
            return <span className="badge bg-warning">Warning</span>;
        } else {
            return <span className="badge bg-danger">Alert</span>;
        }
    };

    const getUtilizationColor = (utilization) => {
        if (utilization >= 100) return 'danger';
        if (utilization >= 80) return 'warning';
        return 'success';
    };

    const getStatusBadgeText = (status) => {
        if (status === 'Good') return <span className="badge bg-success">Good</span>;
        if (status === 'Warning') return <span className="badge bg-warning">Warning</span>;
        return <span className="badge bg-danger">Alert</span>;
       
    };

    const columnDefs = [
        {
            headerName: "Account",
            field: "account_name",
            width: 200,
            sortable: true,
            resizable: true,
            filter: true,
            floatingFilter: true,
            cellStyle: { textAlign: "left" },
            cellRenderer: function (params) {
                return (
                    <>
                        <strong>{params.data.account_name}</strong><br/>
                        <small className="text-muted">{params.data.account_code}</small>
                    </>
                );
            }
        },
        {
            headerName: "Branch",
            field: "branch_name",
            width: 150,
            sortable: true,
            resizable: true,
            filter: true,
            floatingFilter: true,
            cellStyle: { textAlign: "left" }
        },
        {
            headerName: "Period",
            field: "period_type",
            width: 140,
            sortable: true,
            resizable: true,
            filter: true,
            floatingFilter: true,
            cellStyle: { textAlign: "left" },
            cellRenderer: function (params) {

                return(
                    <>
                    <span className="badge bg-info">{params.data.period_type}</span>
                    {params.data.period_name && (
                        <div>
                            <small>{params.data.period_name}</small>
                        </div>
                    )}
                    </>
                )
               
            }
        },
        {
            headerName: "Duration",
            field: "start_date",
            width: 160,
            sortable: true,
            resizable: true,
            filter: true,
            floatingFilter: true,
            cellStyle: { textAlign: "left" },
            cellRenderer: function (params) {
                return (
                    <small>
                        {moment(params.data.start_date).format('DD-MM-YYYY')}<br/>
                        {moment(params.data.end_date).format('DD-MM-YYYY')}
                    </small>
                );
            }
        },
        {
            headerName: "Budgeted Amount",
            field: "budgeted_amount",
            width: 160,
            sortable: true,
            resizable: true,
            filter: true,
            floatingFilter: true,
            cellStyle: { textAlign: "right" },
            cellRenderer: function (params) {
                return (
                    <strong>₹ ${parseFloat(params.value).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</strong>
                );
            }
        },
        {
            headerName: "Actual Spending",
            field: "actual_spending",
            width: 160,
            sortable: true,
            resizable: true,
            filter: true,
            floatingFilter: true,
            cellStyle: { textAlign: "right" },
            cellRenderer: function (params) {
                return `₹ ${parseFloat(params.value).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`;
            }
        },
        {
            headerName: "Variance",
            field: "variance",
            width: 160,
            sortable: true,
            resizable: true,
            filter: true,
            floatingFilter: true,
            cellStyle: function (params) {
                return {
                    textAlign: "right",
                    color: params.value >= 0 ? 'green' : 'red'
                };
            },
            cellRenderer: function (params) {
                const variance = parseFloat(params.value).toLocaleString('en-IN', { minimumFractionDigits: 2 });
                const label = params.value >= 0 ? ' (Under)' : ' (Over)';
                return (
                    <strong>₹ {variance}{label}</strong>
                );
            }
        },
        {
            headerName: "Utilization %",
            field: "utilization_percentage",
            width: 150,
            sortable: true,
            resizable: true,
            filter: true,
            floatingFilter: true,
            cellStyle: { textAlign: "center" },
            cellRenderer: function (params) {
                const utilization = params.value || 0;
                const color = getUtilizationColor(utilization);
                return (
                    <div className="progress" style={{ height: "25px" }}>
                        <div className={`progress-bar bg-${color}`} role="progressbar" style={{ width: `${Math.min(utilization, 100)}%` }}>
                            {parseFloat(utilization).toFixed(1)}%
                        </div>
                    </div>
                );
            }
        },
        {
            headerName: "Status",
            field: "status",
            width: 120,
            sortable: true,
            resizable: true,
            filter: true,
            floatingFilter: true,
            cellStyle: { textAlign: "center" },
            cellRenderer: function (params) {
                return getStatusBadgeText(params.value);
            }
        }
    ];

    const handleDownloadPDF = () => {
        const token = localStorage.getItem("token");

        const params = new URLSearchParams({
            ...filters,
            branch_id: branchId
        }).toString();
        const url = `${baseUrl}/budgetvsactual/pdf?${params}`;

        fetch(url, {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${token}`,
            }
        })
        .then(response => response.blob())
        .then(blob => {
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `BudgetVsActual_${moment().format('DD-MM-YYYY')}.pdf`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
        })
        .catch(error => {
            console.error('Error downloading PDF:', error);
            toast.error('Error downloading PDF');
        });
    };

    // Print sends the same PDF the download button produces straight to the printer.
    const handlePrint = () => {
        const token = localStorage.getItem("token");

        const params = new URLSearchParams({
            ...filters,
            branch_id: branchId
        }).toString();

        printReportUrl(`${baseUrl}/budgetvsactual/pdf?${params}&output=html`, {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${token}`,
            }
        });
    };

    const handleDownloadExcel = () => {
        const token = localStorage.getItem("token");

        const params = new URLSearchParams({
            ...filters,
            branch_id: branchId
        }).toString();
        const url = `${baseUrl}/budgetvsactual/excel?${params}`;

        fetch(url, {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${token}`,
            }
        })
        .then(response => response.blob())
        .then(blob => {
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `BudgetVsActual_${moment().format('DD-MM-YYYY')}.xlsx`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
        })
        .catch(error => {
            console.error('Error downloading Excel:', error);
            toast.error('Error downloading Excel');
        });
    };

    return (
        <div>
            <div className="page-header">
                <h2 className="main-content-title tx-24 mg-b-5">Budget vs Actual Report</h2>
                <ol className="breadcrumb">
                    <li className="breadcrumb-item">
                        <a>Accounts</a>
                    </li>
                    <li className="breadcrumb-item">
                        <a>Budget</a>
                    </li>
                    <li className="breadcrumb-item active" aria-current="page">
                        Budget vs Actual
                    </li>
                </ol>
            </div>

            <div className="content-body">
                <div className="container-fluid">
                    <div className="row">
                        <div className="col-12">
                            <div className="card">
                                <div className="card-header">
                                    <div className="row">
                                        <div className="col-md-6">
                                            <h4 className="card-title">Budget vs Actual Report</h4>
                                        </div>
                                        <div className="col-md-6 text-end">
                                            {reportData && (
                                                <>
                                                    <button className="btn btn-danger me-2" onClick={handleDownloadPDF}>
                                                        <i className="fa fa-file-pdf"></i> Download PDF
                                                    </button>
                                                    <button className="btn btn-success me-2" onClick={handleDownloadExcel}>
                                                        <i className="fa fa-file-excel"></i> Download Excel
                                                    </button>
                                                    <button className="btn btn-primary" onClick={handlePrint}>
                                                        <i className="fa fa-print"></i> Print
                                                    </button>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                </div>
                                <div className="card-body">
                            {/* Filters */}
                            <div className="card mb-4">
                                <div className="card-body">
                                    <div className="row">
                                        <div className="col-md-3 mb-3">
                                            <label className="form-label">Financial Year <span className="text-danger">*</span></label>
                                            <select
                                                className="default-select  form-control wide"
                                                name="financial_year_id"
                                                value={filters.financial_year_id}
                                                onChange={handleFilterChange}
                                            >
                                                <option value="">Select Financial Year</option>
                                                {financialYears.map((year) => (
                                                    <option key={year.id} value={year.id}>
                                                        {year.year_name}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                        <div className="col-md-2 mb-3">
                                            <label className="form-label">Period Type</label>
                                            <select
                                                className="default-select  form-control wide"
                                                name="period_type"
                                                value={filters.period_type}
                                                onChange={handleFilterChange}
                                            >
                                                <option value="">All Types</option>
                                                <option value="Annual">Annual</option>
                                                <option value="Quarterly">Quarterly</option>
                                                <option value="Monthly">Monthly</option>
                                            </select>
                                        </div>
                                        <div className="col-md-2 mb-3">
                                            <label className="form-label">Start Date</label>
                                            <input
                                                type="date"
                                                className="form-control"
                                                name="start_date"
                                                value={filters.start_date}
                                                onChange={handleFilterChange}
                                            />
                                        </div>
                                        <div className="col-md-2 mb-3">
                                            <label className="form-label">End Date</label>
                                            <input
                                                type="date"
                                                className="form-control"
                                                name="end_date"
                                                value={filters.end_date}
                                                onChange={handleFilterChange}
                                            />
                                        </div>
                                    </div>
                                    <div className="row">
                                        <div className="col-md-12 text-end">
                                            <button
                                                className="btn btn-primary"
                                                onClick={fetchReport}
                                                disabled={loading}
                                            >
                                                <i className="fa fa-search"></i> Generate Report
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {loading && (
                <div className="loader-wrapper">
                    <div className="loader"></div>
                    <h3>Loading...Please do not go back or close the window.</h3>
                </div>
            )}

                            {reportData && !loading && (
                                <>
                                    {/* Summary Cards */}
                                    <div className="row mb-4">
                                        <div className="col-md-3">
                                            <div className="card bg-primary text-white">
                                                <div className="card-body">
                                                    <h6>Total Budgeted</h6>
                                                    <h3>₹ {parseFloat(reportData.summary.total_budgeted).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</h3>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="col-md-3">
                                            <div className="card bg-info text-white">
                                                <div className="card-body">
                                                    <h6>Total Actual</h6>
                                                    <h3>₹ {parseFloat(reportData.summary.total_actual).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</h3>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="col-md-3">
                                            <div className={`card ${reportData.summary.total_variance >= 0 ? 'bg-success' : 'bg-danger'} text-white`}>
                                                <div className="card-body">
                                                    <h6>Total Variance</h6>
                                                    <h3>₹ {parseFloat(reportData.summary.total_variance).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</h3>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="col-md-3">
                                            <div className="card bg-secondary text-white">
                                                <div className="card-body">
                                                    <h6>Status Summary</h6>
                                                    <div className="d-flex justify-content-between">
                                                        <span className="badge bg-success">{reportData.summary.count_good} Good</span>
                                                        <span className="badge bg-warning">{reportData.summary.count_warning} Warning</span>
                                                        <span className="badge bg-danger">{reportData.summary.count_alert} Alert</span>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Budget Details Table */}
                                    <div className="ag-theme-alpine ag-style mt-2">
                                        <AgGridReact
                                            columnDefs={columnDefs}
                                            rowData={reportData?.budgets || []}
                                            pagination={true}
                                            paginationPageSize={10}
                                            sortingOrder={["asc", "desc"]}
                                            suppressDragLeaveHidesColumns={true}
                                            domLayout="autoHeight"
                                        />
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
